import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { fetchProducts } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { MessageCircle, Send } from "lucide-react";
import { wa } from "@/lib/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = {
  name: "", company: "", country: "", email: "", phone: "",
  product: "", quantity: "", destination_port: "", incoterm: "FOB",
  packaging: "", specification: "", delivery_timeline: "", message: "", website: "",
};

const QuoteRequest = () => {
  const [f, setF] = useState(empty);
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  useEffect(() => { fetchProducts().then(setProducts); }, []);
  const on = (k) => (e) => setF((x) => ({ ...x, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.company || !f.country || !f.email || !f.phone) { toast.error("Please fill all required fields"); return; }
    setBusy(true);
    try {
      const items = f.product ? [{ product_id: f.product, product_name: products.find((p) => p.id === f.product)?.name || f.product, category: products.find((p) => p.id === f.product)?.category, quantity_mt: f.quantity, packaging: f.packaging, incoterm: f.incoterm, destination_port: f.destination_port }] : [];
      const payload = { name: f.name, company: f.company, country: f.country, email: f.email, phone: f.phone, destination_port: f.destination_port, incoterm: f.incoterm, packaging: f.packaging, delivery_timeline: f.delivery_timeline, message: [f.specification && `Specification: ${f.specification}`, f.message].filter(Boolean).join("\n"), items, source: "quote-page", page: window.location.pathname, website: f.website };
      const { data } = await axios.post(`${API}/enquiries`, payload);
      toast.success("Quote request submitted");
      nav(`/enquiry-confirmed/${data.id}`);
    } catch (err) {
      toast.error("Could not submit. Please try again or WhatsApp us.");
    } finally { setBusy(false); }
  };

  return (
    <div>
      <section className="bg-[#004225] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-[11px] font-eyebrow text-[#D4AF37]"><span className="gold-line mr-3" />Bulk Quote Request</div>
          <h1 className="mt-2 font-display text-4xl sm:text-5xl">Tell Us What You Need. We&apos;ll Work Out the Supply.</h1>
        </div>
      </section>
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <form onSubmit={submit} className="bg-white border border-[#004225]/10 rounded-sm p-6 sm:p-8 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="quote-request-form">
          <div><Label>Name *</Label><Input value={f.name} onChange={on("name")} data-testid="qr-name" required /></div>
          <div><Label>Company *</Label><Input value={f.company} onChange={on("company")} data-testid="qr-company" required /></div>
          <div><Label>Country *</Label><Input value={f.country} onChange={on("country")} data-testid="qr-country" required /></div>
          <div><Label>Email *</Label><Input type="email" value={f.email} onChange={on("email")} data-testid="qr-email" required /></div>
          <div><Label>Phone / WhatsApp *</Label><Input value={f.phone} onChange={on("phone")} data-testid="qr-phone" required /></div>
          <div>
            <Label>Product</Label>
            <Select value={f.product} onValueChange={on("product")}>
              <SelectTrigger data-testid="qr-product"><SelectValue placeholder="Select a product" /></SelectTrigger>
              <SelectContent>
                {products.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Quantity (MT)</Label><Input value={f.quantity} onChange={on("quantity")} placeholder="e.g. 50" /></div>
          <div><Label>Destination Port</Label><Input value={f.destination_port} onChange={on("destination_port")} placeholder="e.g. Jebel Ali" /></div>
          <div><Label>Packaging</Label><Input value={f.packaging} onChange={on("packaging")} placeholder="e.g. 25 kg PP" /></div>
          <div>
            <Label>Incoterm</Label>
            <Select value={f.incoterm} onValueChange={on("incoterm")}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="FOB">FOB</SelectItem>
                <SelectItem value="CFR">CFR</SelectItem>
                <SelectItem value="CIF">CIF</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2"><Label>Specification</Label><Input value={f.specification} onChange={on("specification")} placeholder="e.g. Moisture max 12.5%, broken max 1%" /></div>
          <div><Label>Expected Delivery</Label><Input value={f.delivery_timeline} onChange={on("delivery_timeline")} placeholder="e.g. within 45 days" /></div>
          <div className="sm:col-span-2"><Label>Message</Label><Textarea rows={3} value={f.message} onChange={on("message")} placeholder="Any additional requirements — private label, certifications, samples…" /></div>
          <input type="text" name="website" value={f.website} onChange={on("website")} tabIndex={-1} autoComplete="off" style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0 }} aria-hidden="true" />
          <div className="sm:col-span-2 flex flex-wrap gap-3 pt-2">
            <Button type="submit" disabled={busy} data-testid="qr-submit" className="btn-primary rounded-full px-6 uppercase tracking-widest text-[11px]"><Send className="h-4 w-4 mr-2" />{busy ? "Submitting…" : "Request Bulk Quote"}</Button>
            <a href={wa()} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white px-6 py-3 text-[11px] font-semibold uppercase tracking-widest"><MessageCircle className="h-4 w-4" />Chat on WhatsApp</a>
          </div>
        </form>
      </section>
    </div>
  );
};

export default QuoteRequest;
